/**
 * Team Management Hooks
 *
 * React hooks for role terminology, team assignments, and user goals.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  roleTerminologyAPI,
  teamAssignmentAPI,
  userGoalsAPI,
} from '@/services/api/teamManagement';
import {
  RoleTerminologyMapping,
  RoleTerminologyCreate,
  DEFAULT_ROLE_TERMINOLOGY,
  getRoleDisplayName,
  getRoleAbbreviation,
} from '@/types/roleTerminology';
import {
  TeamAssignment,
  TeamAssignmentCreate,
  TeamMemberWithStats,
  SupervisorTeamSummary,
  OrganizationTeamOverview,
} from '@/types/teamStructure';
import {
  UserGoal,
  UserGoalCreate,
  UserGoalSummary,
  TeamGoalSummary,
  GoalTemplate,
} from '@/types/userGoals';
import { CBORoleType } from '@/types/cboRoles';

// ============================================================================
// ROLE TERMINOLOGY HOOK
// ============================================================================

export interface UseRoleTerminologyReturn {
  terminology: RoleTerminologyMapping[];
  loading: boolean;
  error: string | null;
  getRoleName: (role: CBORoleType, language?: 'en' | 'es') => string;
  getRoleAbbr: (role: CBORoleType) => string;
  updateTerminology: (data: RoleTerminologyCreate) => Promise<RoleTerminologyMapping | null>;
  deleteTerminology: (id: number) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useRoleTerminology(organizationId: number | null): UseRoleTerminologyReturn {
  const [terminology, setTerminology] = useState<RoleTerminologyMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTerminology = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await roleTerminologyAPI.getTerminology(organizationId);
      setTerminology(data);
    } catch (err: any) {
      // If endpoint doesn't exist yet, use empty array (defaults will be used)
      if (err.response?.status === 404) {
        setTerminology([]);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch terminology');
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchTerminology();
  }, [fetchTerminology]);

  const getRoleName = useCallback(
    (role: CBORoleType, language: 'en' | 'es' = 'en') => {
      return getRoleDisplayName(role, language, terminology);
    },
    [terminology]
  );

  const getRoleAbbr = useCallback(
    (role: CBORoleType) => {
      return getRoleAbbreviation(role, terminology);
    },
    [terminology]
  );

  const updateTerminology = useCallback(
    async (data: RoleTerminologyCreate): Promise<RoleTerminologyMapping | null> => {
      if (!organizationId) return null;

      try {
        const result = await roleTerminologyAPI.upsertTerminology(organizationId, data);
        await fetchTerminology();
        return result;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return null;
      }
    },
    [organizationId, fetchTerminology]
  );

  const deleteTerminology = useCallback(
    async (id: number): Promise<boolean> => {
      if (!organizationId) return false;

      try {
        await roleTerminologyAPI.deleteTerminology(organizationId, id);
        await fetchTerminology();
        return true;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return false;
      }
    },
    [organizationId, fetchTerminology]
  );

  return {
    terminology,
    loading,
    error,
    getRoleName,
    getRoleAbbr,
    updateTerminology,
    deleteTerminology,
    refetch: fetchTerminology,
  };
}

// ============================================================================
// TEAM ASSIGNMENTS HOOK
// ============================================================================

export interface UseTeamAssignmentsReturn {
  overview: OrganizationTeamOverview | null;
  assignments: TeamAssignment[];
  loading: boolean;
  error: string | null;
  createAssignment: (data: TeamAssignmentCreate) => Promise<TeamAssignment | null>;
  updateAssignment: (id: number, data: Partial<TeamAssignmentCreate>) => Promise<TeamAssignment | null>;
  deleteAssignment: (id: number) => Promise<boolean>;
  transferStaff: (staffIds: number[], fromSupervisorId: number, toSupervisorId: number) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useTeamAssignments(organizationId: number | null): UseTeamAssignmentsReturn {
  const [overview, setOverview] = useState<OrganizationTeamOverview | null>(null);
  const [assignments, setAssignments] = useState<TeamAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    try {
      const [overviewData, assignmentsData] = await Promise.all([
        teamAssignmentAPI.getTeamOverview(organizationId).catch(() => null),
        teamAssignmentAPI.listAssignments(organizationId, { active_only: true }),
      ]);

      setOverview(overviewData);
      setAssignments(assignmentsData);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch team data');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createAssignment = useCallback(
    async (data: TeamAssignmentCreate): Promise<TeamAssignment | null> => {
      if (!organizationId) return null;

      try {
        const result = await teamAssignmentAPI.createAssignment(organizationId, data);
        await fetchData();
        return result;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return null;
      }
    },
    [organizationId, fetchData]
  );

  const updateAssignment = useCallback(
    async (id: number, data: Partial<TeamAssignmentCreate>): Promise<TeamAssignment | null> => {
      if (!organizationId) return null;

      try {
        const result = await teamAssignmentAPI.updateAssignment(organizationId, id, data);
        await fetchData();
        return result;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return null;
      }
    },
    [organizationId, fetchData]
  );

  const deleteAssignment = useCallback(
    async (id: number): Promise<boolean> => {
      if (!organizationId) return false;

      try {
        await teamAssignmentAPI.deleteAssignment(organizationId, id);
        await fetchData();
        return true;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return false;
      }
    },
    [organizationId, fetchData]
  );

  const transferStaff = useCallback(
    async (staffIds: number[], fromSupervisorId: number, toSupervisorId: number): Promise<boolean> => {
      if (!organizationId) return false;

      try {
        await teamAssignmentAPI.transferStaff(organizationId, {
          staff_ids: staffIds,
          from_supervisor_id: fromSupervisorId,
          to_supervisor_id: toSupervisorId,
        });
        await fetchData();
        return true;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return false;
      }
    },
    [organizationId, fetchData]
  );

  return {
    overview,
    assignments,
    loading,
    error,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    transferStaff,
    refetch: fetchData,
  };
}

// ============================================================================
// SUPERVISOR TEAM HOOK
// ============================================================================

export interface UseSupervisorTeamReturn {
  team: SupervisorTeamSummary | null;
  members: TeamMemberWithStats[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useSupervisorTeam(
  organizationId: number | null,
  supervisorId: number | null
): UseSupervisorTeamReturn {
  const [team, setTeam] = useState<SupervisorTeamSummary | null>(null);
  const [members, setMembers] = useState<TeamMemberWithStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!organizationId || !supervisorId) return;

    setLoading(true);
    setError(null);

    try {
      const [teamData, membersData] = await Promise.all([
        teamAssignmentAPI.getSupervisorTeam(organizationId, supervisorId),
        teamAssignmentAPI.getTeamMembersWithStats(organizationId, supervisorId),
      ]);

      setTeam(teamData);
      setMembers(membersData);
    } catch (err: any) {
      // If endpoints don't exist yet, use mock data
      if (err.response?.status === 404) {
        setTeam(null);
        setMembers([]);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch team');
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId, supervisorId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    team,
    members,
    loading,
    error,
    refetch: fetchData,
  };
}

// ============================================================================
// USER GOALS HOOK
// ============================================================================

export interface UseUserGoalsReturn {
  goals: UserGoal[];
  summary: UserGoalSummary | null;
  loading: boolean;
  error: string | null;
  createGoal: (data: UserGoalCreate) => Promise<UserGoal | null>;
  updateGoal: (goalId: number, data: Partial<UserGoalCreate>) => Promise<UserGoal | null>;
  updateProgress: (goalId: number, currentValue: number, notes?: string) => Promise<UserGoal | null>;
  deleteGoal: (goalId: number) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useUserGoals(
  organizationId: number | null,
  userId: number | null
): UseUserGoalsReturn {
  const [goals, setGoals] = useState<UserGoal[]>([]);
  const [summary, setSummary] = useState<UserGoalSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!organizationId || !userId) return;

    setLoading(true);
    setError(null);

    try {
      const [goalsData, summaryData] = await Promise.all([
        userGoalsAPI.getUserGoals(organizationId, userId),
        userGoalsAPI.getUserGoalSummary(organizationId, userId).catch(() => null),
      ]);

      setGoals(goalsData);
      setSummary(summaryData);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setGoals([]);
        setSummary(null);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch goals');
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createGoal = useCallback(
    async (data: UserGoalCreate): Promise<UserGoal | null> => {
      if (!organizationId) return null;

      try {
        const result = await userGoalsAPI.createGoal(organizationId, data);
        await fetchData();
        return result;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return null;
      }
    },
    [organizationId, fetchData]
  );

  const updateGoal = useCallback(
    async (goalId: number, data: Partial<UserGoalCreate>): Promise<UserGoal | null> => {
      if (!organizationId || !userId) return null;

      try {
        const result = await userGoalsAPI.updateGoal(organizationId, userId, goalId, data);
        await fetchData();
        return result;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return null;
      }
    },
    [organizationId, userId, fetchData]
  );

  const updateProgress = useCallback(
    async (goalId: number, currentValue: number, notes?: string): Promise<UserGoal | null> => {
      if (!organizationId || !userId) return null;

      try {
        const result = await userGoalsAPI.updateProgress(organizationId, userId, goalId, {
          current_value: currentValue,
          notes,
        });
        await fetchData();
        return result;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return null;
      }
    },
    [organizationId, userId, fetchData]
  );

  const deleteGoal = useCallback(
    async (goalId: number): Promise<boolean> => {
      if (!organizationId || !userId) return false;

      try {
        await userGoalsAPI.deleteGoal(organizationId, userId, goalId);
        await fetchData();
        return true;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return false;
      }
    },
    [organizationId, userId, fetchData]
  );

  return {
    goals,
    summary,
    loading,
    error,
    createGoal,
    updateGoal,
    updateProgress,
    deleteGoal,
    refetch: fetchData,
  };
}

// ============================================================================
// TEAM GOALS HOOK (FOR SUPERVISORS)
// ============================================================================

export interface UseTeamGoalsReturn {
  summary: TeamGoalSummary | null;
  loading: boolean;
  error: string | null;
  bulkAssignGoals: (userIds: number[], goalData: Omit<UserGoalCreate, 'user_id'>) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useTeamGoals(
  organizationId: number | null,
  supervisorId: number | null
): UseTeamGoalsReturn {
  const [summary, setSummary] = useState<TeamGoalSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!organizationId || !supervisorId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await userGoalsAPI.getTeamGoalSummary(organizationId, supervisorId);
      setSummary(data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setSummary(null);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch team goals');
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId, supervisorId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const bulkAssignGoals = useCallback(
    async (userIds: number[], goalData: Omit<UserGoalCreate, 'user_id'>): Promise<boolean> => {
      if (!organizationId) return false;

      try {
        await userGoalsAPI.bulkAssignGoals(organizationId, {
          user_ids: userIds,
          ...goalData,
        });
        await fetchData();
        return true;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return false;
      }
    },
    [organizationId, fetchData]
  );

  return {
    summary,
    loading,
    error,
    bulkAssignGoals,
    refetch: fetchData,
  };
}

// ============================================================================
// GOAL TEMPLATES HOOK
// ============================================================================

export interface UseGoalTemplatesReturn {
  templates: GoalTemplate[];
  loading: boolean;
  error: string | null;
  createTemplate: (data: Omit<GoalTemplate, 'id' | 'organization_id' | 'created_at'>) => Promise<GoalTemplate | null>;
  createGoalFromTemplate: (templateId: number, userId: number, overrides?: Partial<UserGoalCreate>) => Promise<UserGoal | null>;
  refetch: () => Promise<void>;
}

export function useGoalTemplates(
  organizationId: number | null,
  programId?: number
): UseGoalTemplatesReturn {
  const [templates, setTemplates] = useState<GoalTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await userGoalsAPI.getTemplates(organizationId, programId);
      setTemplates(data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setTemplates([]);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch templates');
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId, programId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createTemplate = useCallback(
    async (data: Omit<GoalTemplate, 'id' | 'organization_id' | 'created_at'>): Promise<GoalTemplate | null> => {
      if (!organizationId) return null;

      try {
        const result = await userGoalsAPI.createTemplate(organizationId, data);
        await fetchData();
        return result;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return null;
      }
    },
    [organizationId, fetchData]
  );

  const createGoalFromTemplate = useCallback(
    async (templateId: number, userId: number, overrides?: Partial<UserGoalCreate>): Promise<UserGoal | null> => {
      if (!organizationId) return null;

      try {
        const result = await userGoalsAPI.createFromTemplate(organizationId, templateId, userId, overrides);
        return result;
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message);
        return null;
      }
    },
    [organizationId]
  );

  return {
    templates,
    loading,
    error,
    createTemplate,
    createGoalFromTemplate,
    refetch: fetchData,
  };
}
